import { masterChefGenABI } from '@jdiyield/abi'
import { ChainID, GNANA_POOL } from '@jdiyield/constants'
import { APR, PlatformName, PriceList, Token } from '@jdiyield/interfaces'
import BigNumber from 'bignumber.js'
import Web3 from 'web3'

import { getJDIStratInfo } from './features/jdiAuto'
import { getBalanceNumber } from './helper'
import { getAleswapVaultAPR } from './platforms/aleswapVault'
import { getApeAPR } from './platforms/apeswap'
import { getAutoFarmAPR } from './platforms/autoFarm'
import { getBeefyAPR, getPoolBeefy } from './platforms/beefy'
import { getCopycatAPR } from './platforms/copycat'
import { getFirebirdAPR } from './platforms/firebird'
import { getKillSwitchAPR } from './platforms/killswitch'
import { getLatteSwapAPR } from './platforms/latteswap'
import { getMoonpotAPR } from './platforms/moonpots'
import { getMiniChefAPR } from './poolType/miniChef'
import getNodeUrl from './rpcRandom'
import { balanceOf, findToken, makeBatchRequest } from './utils'

export const getAPR = async (
  platformName: PlatformName,
  contractAddress: string,
  functionRewardPerBlockName: string,
  rewardAddress: string,
  stakeTokenAddress: string,
  poolId: number,
  chainID: ChainID,
  tokens: Token[],
  priceList: PriceList,
  getBonusMultiplier?: string,
  lpUSD?: number
): Promise<APR> => {
  try {
    let rewardPerBlock = ''
    let multiplier = '1'
    let allocPoints = ''
    let totalAllocPoints = ''
    if (platformName === PlatformName.JDI_POLYGON || platformName === PlatformName.JDI) {
      const stratData = await getJDIStratInfo(poolId, chainID)

      if (!stratData) return null
      contractAddress = stratData.contractAddress
      functionRewardPerBlockName = stratData?.functionRewardPerBlockName
      poolId = stratData.poolIdStrat
      getBonusMultiplier = stratData?.getBonusMultiplier
      platformName = stratData?.platform.name as any
      rewardAddress = stratData.rewardAddress
    }

    if (platformName === PlatformName.SUSHISWAP_POLYGON || platformName === PlatformName.IRON_POLYGON) {
      ;({ rewardPerBlock, multiplier, allocPoints, totalAllocPoints } = await getMiniChefAPR(
        contractAddress,
        functionRewardPerBlockName,
        poolId,
        chainID
      ))
      multiplier = platformName === PlatformName.IRON_POLYGON ? '2' : multiplier

      return calculateAPR({
        rewardPerBlock,
        multiplier,
        stakeTokenAddress,
        allocPoints,
        totalAllocPoints,
        rewardAddress,
        contractAddress,
        chainID,
        tokens,
        priceList,
        lpUSD
      })
    } else if (platformName === PlatformName.APE_SWAP_POLYGON) {
      ;({ rewardPerBlock, multiplier, allocPoints, totalAllocPoints } = await getApeAPR(
        contractAddress,
        functionRewardPerBlockName,
        poolId,
        chainID
      ))

      return calculateAPR({
        rewardPerBlock,
        multiplier,
        stakeTokenAddress,
        allocPoints,
        totalAllocPoints,
        rewardAddress,
        contractAddress,
        chainID,
        tokens,
        priceList,
        lpUSD
      })
    } else if (platformName === PlatformName.BEEFY) {
      const pools = await getPoolBeefy(chainID)
      const lpId = pools.find((pool: any) => pool.id === poolId)?.id

      return getBeefyAPR(lpId)
    } else if (platformName === PlatformName.QUICKSWAP_POLYGON || platformName === PlatformName.DFYN_POLYGIN) {
      const result = await loadMultipleMaticSynthetixPools({
        chainID,
        contractAddress,
        stakeTokenAddress,
        rewardAddress,
        tokens,
        priceList
      })

      return result
    } else if (platformName === PlatformName.AUTO_FARM || platformName === PlatformName.AUTO_FARM_POLYGON) {
      const result = await getAutoFarmAPR(poolId, chainID)
      const rewardSymbol = findToken(tokens, result?.rewardAddress)?.symbol ?? ''

      return { ...result, rewardSymbol }
    } else if (platformName === PlatformName.KILL_SWITCH) {
      const result = await getKillSwitchAPR(stakeTokenAddress)
      const rewardPrice = await getLpPrice(stakeTokenAddress, chainID, tokens, priceList)
      const rewardSymbol = findToken(tokens, stakeTokenAddress)?.symbol ?? ''

      return { ...result, rewardAddress: stakeTokenAddress, rewardPrice, rewardSymbol }
    } else if (platformName === PlatformName.FIREBIRD) {
      const result = await getFirebirdAPR(stakeTokenAddress, chainID)
      const rewardPrice = await getLpPrice(stakeTokenAddress, chainID, tokens, priceList)
      const rewardSymbol = findToken(tokens, stakeTokenAddress)?.symbol ?? ''
      return { ...result, rewardAddress: stakeTokenAddress, rewardPrice, rewardSymbol }
    } else if (platformName === PlatformName.ALESWAP_VAULT) {
      const result = await getAleswapVaultAPR(stakeTokenAddress)
      const rewardPrice = await getLpPrice(stakeTokenAddress, chainID, tokens, priceList)
      const rewardSymbol = findToken(tokens, stakeTokenAddress)?.symbol ?? ''
      return { ...result, rewardAddress: stakeTokenAddress, rewardPrice, rewardSymbol }
    } else if (platformName === PlatformName.MOONPOTS) {
      const result = await getMoonpotAPR({
        chainID,
        contractAddress,
        stakeTokenAddress,
        rewardAddress,
        tokens,
        priceList
      })

      return result
    } else if (platformName === PlatformName.COPYCAT) {
      ;({ rewardPerBlock, multiplier, allocPoints, totalAllocPoints } = await getCopycatAPR(
        contractAddress,
        poolId,
        chainID,
        getBonusMultiplier
      ))

      return calculateAPR({
        rewardPerBlock,
        multiplier,
        stakeTokenAddress,
        allocPoints,
        totalAllocPoints,
        rewardAddress,
        contractAddress,
        chainID,
        tokens,
        priceList,
        lpUSD
      })
    } else if (platformName === PlatformName.LATTE_SWAP) {
      ;({ rewardPerBlock, multiplier, allocPoints, totalAllocPoints } = await getLatteSwapAPR(
        contractAddress,
        stakeTokenAddress,
        chainID,
        getBonusMultiplier
      ))
      return calculateAPR({
        rewardPerBlock,
        multiplier,
        stakeTokenAddress,
        allocPoints,
        totalAllocPoints,
        rewardAddress,
        contractAddress,
        chainID,
        tokens,
        priceList,
        lpUSD
      })
    } else {
      ;({ rewardPerBlock, multiplier, allocPoints, totalAllocPoints } = await getDefaultAPR(
        contractAddress,
        functionRewardPerBlockName,
        poolId,
        chainID,
        getBonusMultiplier
      ))

      return calculateAPR({
        rewardPerBlock,
        multiplier,
        stakeTokenAddress,
        allocPoints,
        totalAllocPoints,
        rewardAddress,
        contractAddress,
        chainID,
        tokens,
        priceList,
        lpUSD
      })
    }
  } catch (error) {
    console.log(contractAddress, error)
  }
}

async function getDefaultAPR(
  contractAddress: string,
  functionRewardPerBlockName: string,
  poolId: number,
  chainID: ChainID,
  getBonusMultiplier?: string
): Promise<{
  rewardPerBlock: string
  multiplier: string
  allocPoints: string
  totalAllocPoints: string
  stakeTokenAddress: string
}> {
  const RPC_URL = getNodeUrl(chainID)
  const web3 = new Web3(RPC_URL)
  let supportGnana = false
  if (GNANA_POOL.includes(contractAddress)) {
    supportGnana = true
  }
  const ct = new web3.eth.Contract(
    getMasterChefAbi(functionRewardPerBlockName, supportGnana ? 'GNANA' : '') as any,
    contractAddress
  )
  const [poolInfo, rewardPerBlock] = await makeBatchRequest(web3, [
    supportGnana ? ct.methods.poolInfo().call : ct.methods.poolInfo(poolId).call,
    ct.methods[`${functionRewardPerBlockName}`]().call
  ])

  const stakeTokenAddress = poolInfo[0]
  const allocPoints = poolInfo[1]
  const totalAllocPoints =
    (await ct.methods
      .totalAllocPoint()
      .call()
      .catch(() => allocPoints)) || +allocPoints

  let multiplier = '1'
  try {
    if (getBonusMultiplier && getBonusMultiplier !== 'none')
      multiplier = await ct.methods[`${getBonusMultiplier}`]().call()
  } catch (error) {
    multiplier = '1'
  }

  return {
    rewardPerBlock,
    multiplier,
    allocPoints,
    totalAllocPoints,
    stakeTokenAddress
  }
}

export async function getPriceTotalValueLock(
  stakeTokenAddress: string,
  chainID: ChainID,
  tokens: Token[],
  priceList: PriceList
) {
  try {
    const RPC_URL = getNodeUrl(chainID)
    const web3 = new Web3(RPC_URL)
    const lpContract = new web3.eth.Contract(LP_ABI as any, stakeTokenAddress)
    let reserves = null

    try {
      reserves = await lpContract.methods.getReserves().call()
    } catch (e) {
      reserves = null
    }
    if (reserves) {
      const batch = await makeBatchRequest(web3, [
        lpContract.methods.token0().call,
        lpContract.methods.token1().call,
        lpContract.methods.totalSupply().call,
        lpContract.methods.decimals().call
      ])
      const token0 = batch[0] as string
      const token1 = batch[1] as string
      const totalSupply = +batch[2]
      const token0Info = findToken(tokens, token0)
      const token1Info = findToken(tokens, token1)

      const reserve0 = reserves[0] / +`1e${token0Info.decimals || 18}`
      const reserve1 = reserves[1] / +`1e${token1Info.decimals || 18}`

      let price0 = priceList?.[token0Info.address]?.usd || 0 /// TO DO Get Price
      let price1 = priceList?.[token1Info.address]?.usd || 0 /// TO DO Get Price
      if (price0 === 0) {
        price0 = (reserve1 * price1) / reserve0
      }
      if (price1 === 0) {
        price1 = (reserve0 * price0) / reserve1
      }
      const tvlPrice = reserve0 * price0 + reserve1 * price1 // tvl in USD
      const price = tvlPrice / (totalSupply / +`1e${+batch[3] || 18}`)

      return price
    } else {
      // const price = (await getPrice(stakeTokenAddress)) || 0 /// TO DO Get Price
      const tokenInfo = findToken(tokens, stakeTokenAddress)
      const price = priceList?.[tokenInfo?.address]?.usd || 0 /// TO DO Get Price
      return price
    }
  } catch (e) {
    console.log(stakeTokenAddress, e)
    return 0
  }
}

async function getLpPrice(lpAddress: string, chainID: ChainID, tokens: Token[], priceList: PriceList) {
  return (
    priceList?.[lpAddress.toLowerCase()]?.usd || (await getPriceTotalValueLock(lpAddress, chainID, tokens, priceList))
  )
}

async function calculateAPR({
  contractAddress,
  rewardPerBlock,
  multiplier = '1',
  stakeTokenAddress,
  allocPoints,
  totalAllocPoints,
  rewardAddress,
  chainID,
  tokens,
  priceList,
  lpUSD
}: {
  contractAddress: string
  rewardPerBlock: string
  multiplier: string
  stakeTokenAddress: string
  allocPoints: string
  totalAllocPoints: string
  rewardAddress: string
  chainID: ChainID
  tokens: Token[]
  priceList: PriceList
  lpUSD?: number
}) {
  try {
    const rewardToken = findToken(tokens, rewardAddress)
    const stakeToken = findToken(tokens, stakeTokenAddress)
    const rewardDecimals = +`1e${rewardToken?.decimals || 18}`
    const secondPerBlock = chainID === 137 ? 2.1 : 3
    const rewardPerWeek = (((+rewardPerBlock / rewardDecimals) * 604800) / secondPerBlock) * +multiplier
    const poolPrice = lpUSD || (await getLpPrice(stakeTokenAddress, chainID, tokens, priceList))
    const rewardPrice = rewardAddress ? await getLpPrice(rewardAddress, chainID, tokens, priceList) : poolPrice
    const stakeTvl = await balanceOf(contractAddress, stakeTokenAddress, chainID)
    const stakedTvlUSD = +getBalanceNumber(new BigNumber(stakeTvl), stakeToken?.decimals || 18) * poolPrice
    const poolRewardsPerWeek = (+allocPoints / +totalAllocPoints) * rewardPerWeek
    const usdPerWeek = poolRewardsPerWeek * rewardPrice
    const weeklyAPR = (usdPerWeek / stakedTvlUSD) * 100
    const dailyAPR = weeklyAPR / 7
    const monthlyAPR = dailyAPR * 30
    const yearlyAPR = weeklyAPR * 52
    const yearAPY = (Math.pow(1 + dailyAPR / 100, 365) - 1) * 100

    return {
      dailyAPR,
      weeklyAPR,
      monthlyAPR,
      yearlyAPR,
      yearAPY,
      rewardPrice,
      poolPrice,
      stakedTvlUSD
    }
  } catch (error) {
    console.log(contractAddress, error)
  }
}

async function loadMultipleMaticSynthetixPools({
  chainID,
  contractAddress,
  stakeTokenAddress,
  rewardAddress,
  tokens,
  priceList,
  lpUSD
}: {
  chainID: ChainID
  contractAddress: string
  stakeTokenAddress: string
  rewardAddress: string
  tokens: Token[]
  priceList: PriceList
  lpUSD?: number
}) {
  const RPC_URL = getNodeUrl(chainID)
  const web3 = new Web3(RPC_URL)
  const ct = new web3.eth.Contract(MATIC_STAKING_ABI as any, contractAddress)
  const lpContract = new web3.eth.Contract(LP_ABI as any, stakeTokenAddress)

  const [periodFinish, rewardRate, stakeDecimals] = await makeBatchRequest(web3, [
    ct.methods.periodFinish().call,
    ct.methods.rewardRate().call,
    lpContract.methods.decimals().call
  ])

  const rewardToken = findToken(tokens, rewardAddress)
  const rewardTokenDecimals = +`1e${rewardToken?.decimals || 18}`

  const rewardPrice =
    priceList?.[rewardAddress]?.usd || (await getPriceTotalValueLock(rewardAddress, chainID, tokens, priceList))
  const poolPrice = lpUSD || (await getPriceTotalValueLock(stakeTokenAddress, chainID, tokens, priceList))
  const weeklyRewards = Date.now() / 1000 > periodFinish ? 0 : (rewardRate / rewardTokenDecimals) * 604800
  const usdPerWeek = weeklyRewards * rewardPrice

  const stakeTvl = await balanceOf(contractAddress, stakeTokenAddress, chainID)
  const stakedTvlUSD = +getBalanceNumber(new BigNumber(stakeTvl), stakeDecimals) * poolPrice
  const weeklyAPR = (usdPerWeek / stakedTvlUSD) * 100
  const dailyAPR = weeklyAPR / 7
  const monthlyAPR = dailyAPR * 30
  const yearlyAPR = weeklyAPR * 52
  const yearAPY = (Math.pow(1 + dailyAPR / 100, 365) - 1) * 100

  return {
    dailyAPR,
    weeklyAPR,
    monthlyAPR,
    yearlyAPR,
    yearAPY,
    rewardPrice
  }
}

function getMasterChefAbi(functionName = 'cakePerBlock', poolName?: string) {
  return [
    {
      inputs: [
        { internalType: 'contract BananaToken', name: '_banana', type: 'address' },
        { internalType: 'contract BananaSplitBar', name: '_bananaSplit', type: 'address' },
        { internalType: 'address', name: '_devaddr', type: 'address' },
        { internalType: 'uint256', name: '_bananaPerBlock', type: 'uint256' },
        { internalType: 'uint256', name: '_startBlock', type: 'uint256' },
        { internalType: 'uint256', name: '_multiplier', type: 'uint256' }
      ],
      stateMutability: 'nonpayable',
      type: 'constructor'
    },
    {
      anonymous: false,
      inputs: [
        { indexed: true, internalType: 'address', name: 'user', type: 'address' },
        { indexed: true, internalType: 'uint256', name: 'pid', type: 'uint256' },
        { indexed: false, internalType: 'uint256', name: 'amount', type: 'uint256' }
      ],
      name: 'Deposit',
      type: 'event'
    },
    {
      anonymous: false,
      inputs: [
        { indexed: true, internalType: 'address', name: 'user', type: 'address' },
        { indexed: true, internalType: 'uint256', name: 'pid', type: 'uint256' },
        { indexed: false, internalType: 'uint256', name: 'amount', type: 'uint256' }
      ],
      name: 'EmergencyWithdraw',
      type: 'event'
    },
    {
      anonymous: false,
      inputs: [
        { indexed: true, internalType: 'address', name: 'previousOwner', type: 'address' },
        { indexed: true, internalType: 'address', name: 'newOwner', type: 'address' }
      ],
      name: 'OwnershipTransferred',
      type: 'event'
    },
    {
      anonymous: false,
      inputs: [
        { indexed: true, internalType: 'address', name: 'user', type: 'address' },
        { indexed: true, internalType: 'uint256', name: 'pid', type: 'uint256' },
        { indexed: false, internalType: 'uint256', name: 'amount', type: 'uint256' }
      ],
      name: 'Withdraw',
      type: 'event'
    },
    {
      inputs: [],
      name: 'BONUS_MULTIPLIER',
      outputs: [{ internalType: 'uint256', name: '', type: 'uint256' }],
      stateMutability: 'view',
      type: 'function'
    },
    {
      inputs: [
        { internalType: 'uint256', name: '_allocPoint', type: 'uint256' },
        { internalType: 'contract IBEP20', name: '_lpToken', type: 'address' },
        { internalType: 'bool', name: '_withUpdate', type: 'bool' }
      ],
      name: 'add',
      outputs: [],
      stateMutability: 'nonpayable',
      type: 'function'
    },
    {
      inputs: [],
      name: 'cake',
      outputs: [{ internalType: 'contract BananaToken', name: '', type: 'address' }],
      stateMutability: 'view',
      type: 'function'
    },
    {
      inputs: [],
      name: functionName,
      outputs: [{ internalType: 'uint256', name: '', type: 'uint256' }],
      stateMutability: 'view',
      type: 'function'
    },
    {
      inputs: [{ internalType: 'contract IBEP20', name: '_lpToken', type: 'address' }],
      name: 'checkPoolDuplicate',
      outputs: [],
      stateMutability: 'view',
      type: 'function'
    },
    {
      inputs: [
        { internalType: 'uint256', name: '_pid', type: 'uint256' },
        { internalType: 'uint256', name: '_amount', type: 'uint256' }
      ],
      name: 'deposit',
      outputs: [],
      stateMutability: 'nonpayable',
      type: 'function'
    },
    {
      inputs: [{ internalType: 'address', name: '_devaddr', type: 'address' }],
      name: 'dev',
      outputs: [],
      stateMutability: 'nonpayable',
      type: 'function'
    },
    {
      inputs: [],
      name: 'devaddr',
      outputs: [{ internalType: 'address', name: '', type: 'address' }],
      stateMutability: 'view',
      type: 'function'
    },
    {
      inputs: [{ internalType: 'uint256', name: '_pid', type: 'uint256' }],
      name: 'emergencyWithdraw',
      outputs: [],
      stateMutability: 'nonpayable',
      type: 'function'
    },
    {
      inputs: [{ internalType: 'uint256', name: '_amount', type: 'uint256' }],
      name: 'enterStaking',
      outputs: [],
      stateMutability: 'nonpayable',
      type: 'function'
    },
    {
      inputs: [
        { internalType: 'uint256', name: '_from', type: 'uint256' },
        { internalType: 'uint256', name: '_to', type: 'uint256' }
      ],
      name: 'getMultiplier',
      outputs: [{ internalType: 'uint256', name: '', type: 'uint256' }],
      stateMutability: 'view',
      type: 'function'
    },
    {
      inputs: [{ internalType: 'uint256', name: '_pid', type: 'uint256' }],
      name: 'getPoolInfo',
      outputs: [
        { internalType: 'address', name: 'lpToken', type: 'address' },
        { internalType: 'uint256', name: 'allocPoint', type: 'uint256' },
        { internalType: 'uint256', name: 'lastRewardBlock', type: 'uint256' },
        { internalType: 'uint256', name: 'accCakePerShare', type: 'uint256' }
      ],
      stateMutability: 'view',
      type: 'function'
    },
    {
      inputs: [{ internalType: 'uint256', name: '_amount', type: 'uint256' }],
      name: 'leaveStaking',
      outputs: [],
      stateMutability: 'nonpayable',
      type: 'function'
    },
    { inputs: [], name: 'massUpdatePools', outputs: [], stateMutability: 'nonpayable', type: 'function' },
    {
      inputs: [],
      name: 'owner',
      outputs: [{ internalType: 'address', name: '', type: 'address' }],
      stateMutability: 'view',
      type: 'function'
    },
    {
      inputs: [
        { internalType: 'uint256', name: '_pid', type: 'uint256' },
        { internalType: 'address', name: '_user', type: 'address' }
      ],
      name: 'pendingCake',
      outputs: [{ internalType: 'uint256', name: '', type: 'uint256' }],
      stateMutability: 'view',
      type: 'function'
    },
    handleFuncPoolInfo(poolName),
    {
      inputs: [],
      name: 'poolLength',
      outputs: [{ internalType: 'uint256', name: '', type: 'uint256' }],
      stateMutability: 'view',
      type: 'function'
    },
    { inputs: [], name: 'renounceOwnership', outputs: [], stateMutability: 'nonpayable', type: 'function' },
    {
      inputs: [
        { internalType: 'uint256', name: '_pid', type: 'uint256' },
        { internalType: 'uint256', name: '_allocPoint', type: 'uint256' },
        { internalType: 'bool', name: '_withUpdate', type: 'bool' }
      ],
      name: 'set',
      outputs: [],
      stateMutability: 'nonpayable',
      type: 'function'
    },
    {
      inputs: [],
      name: 'startBlock',
      outputs: [{ internalType: 'uint256', name: '', type: 'uint256' }],
      stateMutability: 'view',
      type: 'function'
    },
    {
      inputs: [],
      name: 'syrup',
      outputs: [{ internalType: 'contract BananaSplitBar', name: '', type: 'address' }],
      stateMutability: 'view',
      type: 'function'
    },
    {
      inputs: [],
      name: 'totalAllocPoint',
      outputs: [{ internalType: 'uint256', name: '', type: 'uint256' }],
      stateMutability: 'view',
      type: 'function'
    },
    {
      inputs: [{ internalType: 'address', name: 'newOwner', type: 'address' }],
      name: 'transferOwnership',
      outputs: [],
      stateMutability: 'nonpayable',
      type: 'function'
    },
    {
      inputs: [{ internalType: 'uint256', name: 'multiplierNumber', type: 'uint256' }],
      name: 'updateMultiplier',
      outputs: [],
      stateMutability: 'nonpayable',
      type: 'function'
    },
    {
      inputs: [{ internalType: 'uint256', name: '_pid', type: 'uint256' }],
      name: 'updatePool',
      outputs: [],
      stateMutability: 'nonpayable',
      type: 'function'
    },
    {
      inputs: [
        { internalType: 'uint256', name: '', type: 'uint256' },
        { internalType: 'address', name: '', type: 'address' }
      ],
      name: 'userInfo',
      outputs: [
        { internalType: 'uint256', name: 'amount', type: 'uint256' },
        { internalType: 'uint256', name: 'rewardDebt', type: 'uint256' }
      ],
      stateMutability: 'view',
      type: 'function'
    },
    {
      inputs: [
        { internalType: 'uint256', name: '_pid', type: 'uint256' },
        { internalType: 'uint256', name: '_amount', type: 'uint256' }
      ],
      name: 'withdraw',
      outputs: [],
      stateMutability: 'nonpayable',
      type: 'function'
    }
  ]
}
function handleFuncPoolInfo(farmName?: string) {
  switch (farmName) {
    case 'GNANA':
      return {
        inputs: [],
        name: 'poolInfo',
        outputs: [
          {
            internalType: 'contract IERC20',
            name: 'lpToken',
            type: 'address'
          },
          {
            internalType: 'uint256',
            name: 'allocPoint',
            type: 'uint256'
          },
          {
            internalType: 'uint256',
            name: 'lastRewardBlock',
            type: 'uint256'
          },
          {
            internalType: 'uint256',
            name: 'accRewardTokenPerShare',
            type: 'uint256'
          }
        ],
        stateMutability: 'view',
        type: 'function'
      }

    default:
      return {
        inputs: [{ internalType: 'uint256', name: '', type: 'uint256' }],
        name: 'poolInfo',
        outputs: [
          { internalType: 'contract IBEP20', name: 'lpToken', type: 'address' },
          { internalType: 'uint256', name: 'allocPoint', type: 'uint256' },
          { internalType: 'uint256', name: 'lastRewardBlock', type: 'uint256' },
          { internalType: 'uint256', name: 'accCakePerShare', type: 'uint256' }
        ],
        stateMutability: 'view',
        type: 'function'
      }
  }
}

const LP_ABI = [
  { inputs: [], payable: false, stateMutability: 'nonpayable', type: 'constructor' },
  {
    anonymous: false,
    inputs: [
      { indexed: true, internalType: 'address', name: 'owner', type: 'address' },
      { indexed: true, internalType: 'address', name: 'spender', type: 'address' },
      { indexed: false, internalType: 'uint256', name: 'value', type: 'uint256' }
    ],
    name: 'Approval',
    type: 'event'
  },
  {
    anonymous: false,
    inputs: [
      { indexed: true, internalType: 'address', name: 'sender', type: 'address' },
      { indexed: false, internalType: 'uint256', name: 'amount0', type: 'uint256' },
      { indexed: false, internalType: 'uint256', name: 'amount1', type: 'uint256' },
      { indexed: true, internalType: 'address', name: 'to', type: 'address' }
    ],
    name: 'Burn',
    type: 'event'
  },
  {
    anonymous: false,
    inputs: [
      { indexed: true, internalType: 'address', name: 'sender', type: 'address' },
      { indexed: false, internalType: 'uint256', name: 'amount0', type: 'uint256' },
      { indexed: false, internalType: 'uint256', name: 'amount1', type: 'uint256' }
    ],
    name: 'Mint',
    type: 'event'
  },
  {
    anonymous: false,
    inputs: [
      { indexed: true, internalType: 'address', name: 'sender', type: 'address' },
      { indexed: false, internalType: 'uint256', name: 'amount0In', type: 'uint256' },
      { indexed: false, internalType: 'uint256', name: 'amount1In', type: 'uint256' },
      { indexed: false, internalType: 'uint256', name: 'amount0Out', type: 'uint256' },
      { indexed: false, internalType: 'uint256', name: 'amount1Out', type: 'uint256' },
      { indexed: true, internalType: 'address', name: 'to', type: 'address' }
    ],
    name: 'Swap',
    type: 'event'
  },
  {
    anonymous: false,
    inputs: [
      { indexed: false, internalType: 'uint112', name: 'reserve0', type: 'uint112' },
      { indexed: false, internalType: 'uint112', name: 'reserve1', type: 'uint112' }
    ],
    name: 'Sync',
    type: 'event'
  },
  {
    anonymous: false,
    inputs: [
      { indexed: true, internalType: 'address', name: 'from', type: 'address' },
      { indexed: true, internalType: 'address', name: 'to', type: 'address' },
      { indexed: false, internalType: 'uint256', name: 'value', type: 'uint256' }
    ],
    name: 'Transfer',
    type: 'event'
  },
  {
    constant: true,
    inputs: [],
    name: 'DOMAIN_SEPARATOR',
    outputs: [{ internalType: 'bytes32', name: '', type: 'bytes32' }],
    payable: false,
    stateMutability: 'view',
    type: 'function'
  },
  {
    constant: true,
    inputs: [],
    name: 'MINIMUM_LIQUIDITY',
    outputs: [{ internalType: 'uint256', name: '', type: 'uint256' }],
    payable: false,
    stateMutability: 'view',
    type: 'function'
  },
  {
    constant: true,
    inputs: [],
    name: 'PERMIT_TYPEHASH',
    outputs: [{ internalType: 'bytes32', name: '', type: 'bytes32' }],
    payable: false,
    stateMutability: 'view',
    type: 'function'
  },
  {
    constant: true,
    inputs: [
      { internalType: 'address', name: '', type: 'address' },
      { internalType: 'address', name: '', type: 'address' }
    ],
    name: 'allowance',
    outputs: [{ internalType: 'uint256', name: '', type: 'uint256' }],
    payable: false,
    stateMutability: 'view',
    type: 'function'
  },
  {
    constant: false,
    inputs: [
      { internalType: 'address', name: 'spender', type: 'address' },
      { internalType: 'uint256', name: 'value', type: 'uint256' }
    ],
    name: 'approve',
    outputs: [{ internalType: 'bool', name: '', type: 'bool' }],
    payable: false,
    stateMutability: 'nonpayable',
    type: 'function'
  },
  {
    constant: true,
    inputs: [{ internalType: 'address', name: '', type: 'address' }],
    name: 'balanceOf',
    outputs: [{ internalType: 'uint256', name: '', type: 'uint256' }],
    payable: false,
    stateMutability: 'view',
    type: 'function'
  },
  {
    constant: false,
    inputs: [{ internalType: 'address', name: 'to', type: 'address' }],
    name: 'burn',
    outputs: [
      { internalType: 'uint256', name: 'amount0', type: 'uint256' },
      { internalType: 'uint256', name: 'amount1', type: 'uint256' }
    ],
    payable: false,
    stateMutability: 'nonpayable',
    type: 'function'
  },
  {
    constant: true,
    inputs: [],
    name: 'decimals',
    outputs: [{ internalType: 'uint8', name: '', type: 'uint8' }],
    payable: false,
    stateMutability: 'view',
    type: 'function'
  },
  {
    constant: true,
    inputs: [],
    name: 'factory',
    outputs: [{ internalType: 'address', name: '', type: 'address' }],
    payable: false,
    stateMutability: 'view',
    type: 'function'
  },
  {
    constant: true,
    inputs: [],
    name: 'getReserves',
    outputs: [
      { internalType: 'uint112', name: '_reserve0', type: 'uint112' },
      { internalType: 'uint112', name: '_reserve1', type: 'uint112' },
      { internalType: 'uint32', name: '_blockTimestampLast', type: 'uint32' }
    ],
    payable: false,
    stateMutability: 'view',
    type: 'function'
  },
  {
    constant: false,
    inputs: [
      { internalType: 'address', name: '_token0', type: 'address' },
      { internalType: 'address', name: '_token1', type: 'address' }
    ],
    name: 'initialize',
    outputs: [],
    payable: false,
    stateMutability: 'nonpayable',
    type: 'function'
  },
  {
    constant: true,
    inputs: [],
    name: 'kLast',
    outputs: [{ internalType: 'uint256', name: '', type: 'uint256' }],
    payable: false,
    stateMutability: 'view',
    type: 'function'
  },
  {
    constant: false,
    inputs: [{ internalType: 'address', name: 'to', type: 'address' }],
    name: 'mint',
    outputs: [{ internalType: 'uint256', name: 'liquidity', type: 'uint256' }],
    payable: false,
    stateMutability: 'nonpayable',
    type: 'function'
  },
  {
    constant: true,
    inputs: [],
    name: 'name',
    outputs: [{ internalType: 'string', name: '', type: 'string' }],
    payable: false,
    stateMutability: 'view',
    type: 'function'
  },
  {
    constant: true,
    inputs: [{ internalType: 'address', name: '', type: 'address' }],
    name: 'nonces',
    outputs: [{ internalType: 'uint256', name: '', type: 'uint256' }],
    payable: false,
    stateMutability: 'view',
    type: 'function'
  },
  {
    constant: false,
    inputs: [
      { internalType: 'address', name: 'owner', type: 'address' },
      { internalType: 'address', name: 'spender', type: 'address' },
      { internalType: 'uint256', name: 'value', type: 'uint256' },
      { internalType: 'uint256', name: 'deadline', type: 'uint256' },
      { internalType: 'uint8', name: 'v', type: 'uint8' },
      { internalType: 'bytes32', name: 'r', type: 'bytes32' },
      { internalType: 'bytes32', name: 's', type: 'bytes32' }
    ],
    name: 'permit',
    outputs: [],
    payable: false,
    stateMutability: 'nonpayable',
    type: 'function'
  },
  {
    constant: true,
    inputs: [],
    name: 'price0CumulativeLast',
    outputs: [{ internalType: 'uint256', name: '', type: 'uint256' }],
    payable: false,
    stateMutability: 'view',
    type: 'function'
  },
  {
    constant: true,
    inputs: [],
    name: 'price1CumulativeLast',
    outputs: [{ internalType: 'uint256', name: '', type: 'uint256' }],
    payable: false,
    stateMutability: 'view',
    type: 'function'
  },
  {
    constant: false,
    inputs: [{ internalType: 'address', name: 'to', type: 'address' }],
    name: 'skim',
    outputs: [],
    payable: false,
    stateMutability: 'nonpayable',
    type: 'function'
  },
  {
    constant: false,
    inputs: [
      { internalType: 'uint256', name: 'amount0Out', type: 'uint256' },
      { internalType: 'uint256', name: 'amount1Out', type: 'uint256' },
      { internalType: 'address', name: 'to', type: 'address' },
      { internalType: 'bytes', name: 'data', type: 'bytes' }
    ],
    name: 'swap',
    outputs: [],
    payable: false,
    stateMutability: 'nonpayable',
    type: 'function'
  },
  {
    constant: true,
    inputs: [],
    name: 'symbol',
    outputs: [{ internalType: 'string', name: '', type: 'string' }],
    payable: false,
    stateMutability: 'view',
    type: 'function'
  },
  {
    constant: false,
    inputs: [],
    name: 'sync',
    outputs: [],
    payable: false,
    stateMutability: 'nonpayable',
    type: 'function'
  },
  {
    constant: true,
    inputs: [],
    name: 'token0',
    outputs: [{ internalType: 'address', name: '', type: 'address' }],
    payable: false,
    stateMutability: 'view',
    type: 'function'
  },
  {
    constant: true,
    inputs: [],
    name: 'token1',
    outputs: [{ internalType: 'address', name: '', type: 'address' }],
    payable: false,
    stateMutability: 'view',
    type: 'function'
  },
  {
    constant: true,
    inputs: [],
    name: 'totalSupply',
    outputs: [{ internalType: 'uint256', name: '', type: 'uint256' }],
    payable: false,
    stateMutability: 'view',
    type: 'function'
  },
  {
    constant: false,
    inputs: [
      { internalType: 'address', name: 'to', type: 'address' },
      { internalType: 'uint256', name: 'value', type: 'uint256' }
    ],
    name: 'transfer',
    outputs: [{ internalType: 'bool', name: '', type: 'bool' }],
    payable: false,
    stateMutability: 'nonpayable',
    type: 'function'
  },
  {
    constant: false,
    inputs: [
      { internalType: 'address', name: 'from', type: 'address' },
      { internalType: 'address', name: 'to', type: 'address' },
      { internalType: 'uint256', name: 'value', type: 'uint256' }
    ],
    name: 'transferFrom',
    outputs: [{ internalType: 'bool', name: '', type: 'bool' }],
    payable: false,
    stateMutability: 'nonpayable',
    type: 'function'
  }
]

const MATIC_STAKING_ABI = [
  {
    inputs: [
      { internalType: 'address', name: '_rewardsDistribution', type: 'address' },
      { internalType: 'address', name: '_rewardsToken', type: 'address' },
      { internalType: 'address', name: '_stakingToken', type: 'address' }
    ],
    payable: false,
    stateMutability: 'nonpayable',
    type: 'constructor'
  },
  {
    anonymous: false,
    inputs: [{ indexed: false, internalType: 'uint256', name: 'reward', type: 'uint256' }],
    name: 'RewardAdded',
    type: 'event'
  },
  {
    anonymous: false,
    inputs: [
      { indexed: true, internalType: 'address', name: 'user', type: 'address' },
      { indexed: false, internalType: 'uint256', name: 'reward', type: 'uint256' }
    ],
    name: 'RewardPaid',
    type: 'event'
  },
  {
    anonymous: false,
    inputs: [
      { indexed: true, internalType: 'address', name: 'user', type: 'address' },
      { indexed: false, internalType: 'uint256', name: 'amount', type: 'uint256' }
    ],
    name: 'Staked',
    type: 'event'
  },
  {
    anonymous: false,
    inputs: [
      { indexed: true, internalType: 'address', name: 'user', type: 'address' },
      { indexed: false, internalType: 'uint256', name: 'amount', type: 'uint256' }
    ],
    name: 'Withdrawn',
    type: 'event'
  },
  {
    constant: true,
    inputs: [{ internalType: 'address', name: 'account', type: 'address' }],
    name: 'balanceOf',
    outputs: [{ internalType: 'uint256', name: '', type: 'uint256' }],
    payable: false,
    stateMutability: 'view',
    type: 'function'
  },
  {
    constant: true,
    inputs: [{ internalType: 'address', name: 'account', type: 'address' }],
    name: 'earned',
    outputs: [{ internalType: 'uint256', name: '', type: 'uint256' }],
    payable: false,
    stateMutability: 'view',
    type: 'function'
  },
  {
    constant: false,
    inputs: [],
    name: 'exit',
    outputs: [],
    payable: false,
    stateMutability: 'nonpayable',
    type: 'function'
  },
  {
    constant: false,
    inputs: [],
    name: 'getReward',
    outputs: [],
    payable: false,
    stateMutability: 'nonpayable',
    type: 'function'
  },
  {
    constant: true,
    inputs: [],
    name: 'getRewardForDuration',
    outputs: [{ internalType: 'uint256', name: '', type: 'uint256' }],
    payable: false,
    stateMutability: 'view',
    type: 'function'
  },
  {
    constant: true,
    inputs: [],
    name: 'lastTimeRewardApplicable',
    outputs: [{ internalType: 'uint256', name: '', type: 'uint256' }],
    payable: false,
    stateMutability: 'view',
    type: 'function'
  },
  {
    constant: true,
    inputs: [],
    name: 'lastUpdateTime',
    outputs: [{ internalType: 'uint256', name: '', type: 'uint256' }],
    payable: false,
    stateMutability: 'view',
    type: 'function'
  },
  {
    constant: false,
    inputs: [{ internalType: 'uint256', name: 'reward', type: 'uint256' }],
    name: 'notifyRewardAmount',
    outputs: [],
    payable: false,
    stateMutability: 'nonpayable',
    type: 'function'
  },
  {
    constant: true,
    inputs: [],
    name: 'periodFinish',
    outputs: [{ internalType: 'uint256', name: '', type: 'uint256' }],
    payable: false,
    stateMutability: 'view',
    type: 'function'
  },
  {
    constant: true,
    inputs: [],
    name: 'rewardPerToken',
    outputs: [{ internalType: 'uint256', name: '', type: 'uint256' }],
    payable: false,
    stateMutability: 'view',
    type: 'function'
  },
  {
    constant: true,
    inputs: [],
    name: 'rewardPerTokenStored',
    outputs: [{ internalType: 'uint256', name: '', type: 'uint256' }],
    payable: false,
    stateMutability: 'view',
    type: 'function'
  },
  {
    constant: true,
    inputs: [],
    name: 'rewardRate',
    outputs: [{ internalType: 'uint256', name: '', type: 'uint256' }],
    payable: false,
    stateMutability: 'view',
    type: 'function'
  },
  {
    constant: true,
    inputs: [{ internalType: 'address', name: '', type: 'address' }],
    name: 'rewards',
    outputs: [{ internalType: 'uint256', name: '', type: 'uint256' }],
    payable: false,
    stateMutability: 'view',
    type: 'function'
  },
  {
    constant: true,
    inputs: [],
    name: 'rewardsDistribution',
    outputs: [{ internalType: 'address', name: '', type: 'address' }],
    payable: false,
    stateMutability: 'view',
    type: 'function'
  },
  {
    constant: true,
    inputs: [],
    name: 'rewardsDuration',
    outputs: [{ internalType: 'uint256', name: '', type: 'uint256' }],
    payable: false,
    stateMutability: 'view',
    type: 'function'
  },
  {
    constant: true,
    inputs: [],
    name: 'rewardsToken',
    outputs: [{ internalType: 'contract IERC20', name: '', type: 'address' }],
    payable: false,
    stateMutability: 'view',
    type: 'function'
  },
  {
    constant: false,
    inputs: [{ internalType: 'uint256', name: 'amount', type: 'uint256' }],
    name: 'stake',
    outputs: [],
    payable: false,
    stateMutability: 'nonpayable',
    type: 'function'
  },
  {
    constant: false,
    inputs: [
      { internalType: 'uint256', name: 'amount', type: 'uint256' },
      { internalType: 'uint256', name: 'deadline', type: 'uint256' },
      { internalType: 'uint8', name: 'v', type: 'uint8' },
      { internalType: 'bytes32', name: 'r', type: 'bytes32' },
      { internalType: 'bytes32', name: 's', type: 'bytes32' }
    ],
    name: 'stakeWithPermit',
    outputs: [],
    payable: false,
    stateMutability: 'nonpayable',
    type: 'function'
  },
  {
    constant: true,
    inputs: [],
    name: 'stakingToken',
    outputs: [{ internalType: 'contract IERC20', name: '', type: 'address' }],
    payable: false,
    stateMutability: 'view',
    type: 'function'
  },
  {
    constant: true,
    inputs: [],
    name: 'totalSupply',
    outputs: [{ internalType: 'uint256', name: '', type: 'uint256' }],
    payable: false,
    stateMutability: 'view',
    type: 'function'
  },
  {
    constant: true,
    inputs: [{ internalType: 'address', name: '', type: 'address' }],
    name: 'userRewardPerTokenPaid',
    outputs: [{ internalType: 'uint256', name: '', type: 'uint256' }],
    payable: false,
    stateMutability: 'view',
    type: 'function'
  },
  {
    constant: false,
    inputs: [{ internalType: 'uint256', name: 'amount', type: 'uint256' }],
    name: 'withdraw',
    outputs: [],
    payable: false,
    stateMutability: 'nonpayable',
    type: 'function'
  }
]
